import { useCallback, useMemo, useState } from 'react';
import { type ButtonType, type ErpInputTableActions, ErpInputTableV2 } from '@/components/custom/erp/input-table-v2';
import { AritoInputTableProps } from '@/components/custom/arito/input-table/types';
import { INTERNAL_ROW_ID, INTERNAL_ROW_INDEX, stripInternalFields } from './utils';
import { buildAritoInputTableColumns } from './column-builder';
import { Button } from '@/components/ui/button';
import { inputTableClassName } from './style';
import { useRows } from './hooks';
import { cn } from '@/lib/utils';

export function AritoInputTable<T = any>({
  rows,
  value,
  columns,
  mode = 'add',
  className,
  tableActionButtons,
  pageSize = 10,
  onSave,
  onCancel,
  onChange,
  onAdd,
  onRowSelect,
  onRowDoubleClick,
  loading,
  errorMessage
}: AritoInputTableProps<T>) {
  const sourceRows = value ?? rows ?? [];
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);

  const { internalRows, setInternalRows, handleAddRow, handleDeleteRow, handleCopyRow, handlePasteRow, handleMoveRow } =
    useRows<T>(sourceRows, onChange);

  const updateRows = useCallback(
    (nextRows: T[]) => {
      setInternalRows(nextRows);
      onChange?.(nextRows);
    },
    [onChange, setInternalRows]
  );

  const updateCellValue = useCallback(
    (rowId: string, field: string, nextValue: unknown) => {
      const rowIndex = Number(rowId);
      if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= internalRows.length) return;

      const nextRows = [...internalRows];
      nextRows[rowIndex] = {
        ...(nextRows[rowIndex] as Record<string, unknown>),
        [field]: nextValue
      } as T;

      updateRows(nextRows);
    },
    [internalRows, updateRows]
  );

  const resolvedColumns = useMemo(
    () => buildAritoInputTableColumns({ columns, mode, updateCellValue }),
    [columns, mode, updateCellValue]
  );

  const displayRows = useMemo(
    () =>
      internalRows.map((row, index) => ({
        ...(row as Record<string, unknown>),
        [INTERNAL_ROW_ID]: String(index),
        [INTERNAL_ROW_INDEX]: index
      })),
    [internalRows]
  );

  const selectedIndexes = useMemo(
    () => selectedRowIds.map(Number).filter(index => Number.isInteger(index) && index >= 0),
    [selectedRowIds]
  );

  const handleAdd = useCallback(() => {
    onAdd?.();
    handleAddRow();
  }, [handleAddRow, onAdd]);

  const actionOrder = useMemo(
    () => (tableActionButtons?.filter(Boolean) as ButtonType[] | undefined) ?? undefined,
    [tableActionButtons]
  );

  const actions = useMemo<ErpInputTableActions>(() => {
    const actionMap: ErpInputTableActions = {};
    const requestedActions = actionOrder ?? ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'pin'];

    if (requestedActions.includes('add')) {
      actionMap.add = handleAdd;
    }

    if (requestedActions.includes('delete')) {
      actionMap.delete = () => handleDeleteRow(selectedIndexes);
    }

    if (requestedActions.includes('copy')) {
      actionMap.copy = () => handleCopyRow(selectedIndexes[0]);
    }

    if (requestedActions.includes('paste')) {
      actionMap.paste = () => handlePasteRow(selectedIndexes[0]);
    }

    if (requestedActions.includes('moveUp')) {
      actionMap.moveUp = () => handleMoveRow(selectedIndexes[0], 'up');
    }

    if (requestedActions.includes('moveDown')) {
      actionMap.moveDown = () => handleMoveRow(selectedIndexes[0], 'down');
    }

    if (requestedActions.includes('pin')) {
      actionMap.pin = () => {};
    }

    if (requestedActions.includes('export')) {
      actionMap.export = () => {};
    }

    if (requestedActions.includes('refresh')) {
      actionMap.refresh = () => {};
    }

    return actionMap;
  }, [actionOrder, handleAdd, handleCopyRow, handleDeleteRow, handleMoveRow, handlePasteRow, selectedIndexes]);

  const toolbar =
    errorMessage || onCancel || onSave ? (
      <div className='ml-auto flex items-center gap-2'>
        {errorMessage && <div className='text-xs font-medium text-red-500'>{errorMessage}</div>}
        {mode === 'add' && onCancel && (
          <Button
            variant='outline'
            size='sm'
            onClick={onCancel}
            className='h-7 border-gray-300 px-3 text-gray-700 hover:border-gray-400 hover:bg-transparent'
          >
            Hủy
          </Button>
        )}
        {mode === 'add' && onSave && (
          <Button size='sm' onClick={onSave} className='h-7 bg-main px-3 text-white hover:bg-main-dark'>
            Lưu
          </Button>
        )}
      </div>
    ) : undefined;

  return (
    <div data-excel-operation-overlay-root className={cn('flex h-72 flex-col', className)}>
      <ErpInputTableV2
        rows={displayRows}
        columns={resolvedColumns as any}
        className='h-full'
        tableClassName={inputTableClassName}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        actions={actions}
        actionOrder={actionOrder}
        actionsDisabled={mode === 'view'}
        toolbar={toolbar}
        selectedRowId={selectedRowIds[0]}
        getRowId={row => String(row[INTERNAL_ROW_ID] ?? '')}
        onRowClick={params => {
          setSelectedRowIds([params.id]);
          onRowSelect?.(stripInternalFields(params.row as T));
        }}
        onRowDoubleClick={params => {
          onRowDoubleClick?.(stripInternalFields(params.row as T));
        }}
        loading={loading}
        hideHeader={false}
        showIndexColumn
        hideEmptyState
        disableColumnFilters={mode !== 'search'}
        rowHeight={34}
        widthMode='content'
        scrollMode='auto'
      />
    </div>
  );
}
